import React from "react";

import {
    Box,
    Card,
    CardContent,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";

import CustomButton from "../../helper-components/input/CustomButton";
import { queryAdminSettings, updateAdminSettings } from "../../helper-functions/api/settingsQueries";
import { exportMenuData, queryMenu, refreshMenuFromSheets } from "../../helper-functions/api/menuQueries";
import { importSheetsExportFolder, importSheetsExportZip } from "../../helper-functions/api/imageQueries";
import { useLanguage } from "../../config/language/language";
import { VENUES } from "../../helper-functions/shared/guestPrefs";

export default function AdminSettings(props) {
    const LANG = useLanguage();
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [importing, setImporting] = React.useState(false);
    const [refreshingMenu, setRefreshingMenu] = React.useState(false);
    const [exportingMenu, setExportingMenu] = React.useState(false);
    const [importResult, setImportResult] = React.useState(null);
    const fileInputRef = React.useRef(null);
    const [form, setForm] = React.useState({
        authEnabled: true,
        publicAccessEnabled: false,
        publicAccessHours: 4,
        venue: "Tallinn",
        publicAccessUntil: null,
    });

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!props.roles.isAdmin) {
                setLoading(false);
                return;
            }
            const data = await queryAdminSettings(props);
            if (!cancelled && data) {
                setForm((prev) => ({
                    ...prev,
                    authEnabled: data.authEnabled,
                    publicAccessEnabled: data.publicAccessEnabled,
                    publicAccessUntil: data.publicAccessUntil,
                    venue: data.venue || "Tallinn",
                }));
            }
            if (!cancelled) setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.roles.isAdmin]);

    if (!props.roles.isAdmin) {
        return (
            <Box sx={{ mt: 4 }}>
                <Typography color="error">{LANG.ADMIN_DENIED}</Typography>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    const handleImportResult = (result) => {
        if (!result) return;
        setImportResult(result);
        queryMenu(props);
        props.showSnackbar?.({
            color: "success",
            duration: 6000,
            message: LANG.adminImagesImported(result.imported, result.mapped)
        });
    };

    const handleZipUpload = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setImporting(true);
        try {
            handleImportResult(await importSheetsExportZip(props, file));
        } finally {
            setImporting(false);
        }
    };

    const handleFolderImport = async () => {
        setImporting(true);
        try {
            handleImportResult(await importSheetsExportFolder(props));
        } finally {
            setImporting(false);
        }
    };

    const handleMenuRefresh = async () => {
        setRefreshingMenu(true);
        try {
            await refreshMenuFromSheets(props, form.venue);
        } finally {
            setRefreshingMenu(false);
        }
    };

    const handleMenuExport = async () => {
        setExportingMenu(true);
        try {
            const data = await exportMenuData(props, form.venue);
            const text = data?.text;
            if (!text) {
                props.showSnackbar?.({
                    color: "error",
                    message: LANG.SNACKBAR_ERROR_MENU_EXPORT,
                });
                return;
            }
            await navigator.clipboard.writeText(text);
            props.showSnackbar?.({
                color: "success",
                message: LANG.SNACKBAR_SUCCESS_MENU_EXPORT,
            });
        } catch {
            props.showSnackbar?.({
                color: "error",
                message: LANG.SNACKBAR_ERROR_MENU_EXPORT,
            });
        } finally {
            setExportingMenu(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                authEnabled: form.authEnabled,
                publicAccessEnabled: form.publicAccessEnabled,
                venue: form.venue,
            };
            if (form.publicAccessEnabled && form.publicAccessHours) {
                payload.publicAccessHours = Number(form.publicAccessHours);
            }
            if (!form.publicAccessEnabled) {
                payload.publicAccessUntil = "";
            }

            const updated = await updateAdminSettings(props, payload);
            if (updated) {
                setForm((prev) => ({
                    ...prev,
                    authEnabled: updated.authEnabled,
                    publicAccessEnabled: updated.publicAccessEnabled,
                    publicAccessUntil: updated.publicAccessUntil,
                    venue: updated.venue,
                }));
                queryMenu(props, updated.venue || form.venue);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box sx={{ mt: 2, maxWidth: 560 }}>
            <Typography variant="h4" gutterBottom>
                {LANG.ADMIN_TITLE}
            </Typography>

            <Card>
                <CardContent>
                    <Stack spacing={2}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.authEnabled}
                                    onChange={(e) => setForm((f) => ({ ...f, authEnabled: e.target.checked }))}
                                />
                            }
                            label={LANG.ADMIN_AUTH_ENABLED}
                        />

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.publicAccessEnabled}
                                    onChange={(e) => setForm((f) => ({ ...f, publicAccessEnabled: e.target.checked }))}
                                />
                            }
                            label={LANG.ADMIN_PUBLIC_ACCESS}
                        />

                        {form.publicAccessEnabled && (
                            <TextField
                                type="number"
                                label={LANG.ADMIN_PUBLIC_HOURS}
                                value={form.publicAccessHours}
                                onChange={(e) => setForm((f) => ({ ...f, publicAccessHours: e.target.value }))}
                                inputProps={{ min: 0.25, step: 0.25 }}
                                helperText={
                                    form.publicAccessUntil
                                        ? `${LANG.ADMIN_PUBLIC_UNTIL}: ${new Date(form.publicAccessUntil).toLocaleString()}`
                                        : " "
                                }
                            />
                        )}

                        <TextField
                            select
                            label={LANG.ADMIN_VENUE}
                            value={form.venue}
                            helperText={LANG.ADMIN_VENUE_HELP}
                            onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))}>
                            {VENUES.map((v) => (
                                <MenuItem key={v} value={v}>
                                    {v}
                                </MenuItem>
                            ))}
                        </TextField>

                        <CustomButton
                            {...props}
                            title={LANG.ADMIN_SAVE}
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving}
                        />
                    </Stack>
                </CardContent>
            </Card>

            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Stack spacing={2}>
                        <Typography variant="h6">{LANG.ADMIN_MENU_TITLE}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {LANG.ADMIN_MENU_HELP}
                        </Typography>
                        <CustomButton
                            {...props}
                            title={LANG.ADMIN_MENU_REFRESH}
                            variant="contained"
                            onClick={handleMenuRefresh}
                            disabled={refreshingMenu || exportingMenu}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {LANG.ADMIN_MENU_EXPORT_HELP}
                        </Typography>
                        <CustomButton
                            {...props}
                            title={LANG.ADMIN_MENU_EXPORT}
                            variant="outlined"
                            onClick={handleMenuExport}
                            disabled={exportingMenu || refreshingMenu}
                        />
                        {(refreshingMenu || exportingMenu) && <CircularProgress size={24} />}
                    </Stack>
                </CardContent>
            </Card>

            <Card sx={{ mt: 3 }}>
                <CardContent>
                    <Stack spacing={2}>
                        <Typography variant="h6">{LANG.ADMIN_IMAGES_TITLE}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {LANG.ADMIN_IMAGES_HELP}
                        </Typography>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".zip,application/zip"
                            hidden
                            onChange={handleZipUpload}
                        />
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                            <CustomButton
                                {...props}
                                title={LANG.ADMIN_IMAGES_UPLOAD}
                                variant="contained"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={importing}
                            />
                            <CustomButton
                                {...props}
                                title={LANG.ADMIN_IMAGES_IMPORT_FOLDER}
                                variant="outlined"
                                onClick={handleFolderImport}
                                disabled={importing}
                            />
                        </Stack>
                        {importing && <CircularProgress size={24} />}
                        {importResult && (
                            <Typography variant="body2">
                                {LANG.ADMIN_IMAGES_RESULT}: {importResult.imported}/{importResult.mapped} {LANG.ADMIN_IMAGES_CACHED}
                                {importResult.missingCount ? ` (${importResult.missingCount} missing)` : ""}
                            </Typography>
                        )}
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
}
